import React, { ReactNode, createContext, useContext, useMemo } from 'react'
import {
  NoraAttachmentItem,
  NoraUploadedAttachmentPreview,
  useNoraAttachmentUpload,
} from '@screens/Nora/hooks/useNoraAttachmentUpload'

interface NoraFileAttachmentsContextValue {
  attachments: NoraAttachmentItem[]
  canAttachMore: boolean
  clearAttachments: () => void
  isUploading: boolean
  openAttachmentPicker: () => void
  removeAttachment: (localId: string) => void
  getUploadedAttachmentPreviews: () => NoraUploadedAttachmentPreview[]
  getUploadedFileIds: () => string[]
  uploadedAttachmentPreviews: NoraUploadedAttachmentPreview[]
  uploadedFileIds: string[]
  uploadAttachmentsAndGetUserFileIds: () => Promise<string[]>
}

const NoraFileAttachmentsContext = createContext<NoraFileAttachmentsContextValue | null>(null)

export const NoraFileAttachmentsProvider = ({ children }: { children: ReactNode }) => {
  const {
    attachments,
    canAttachMore,
    clearAttachments,
    isUploading,
    openAttachmentPicker,
    removeAttachment,
    getUploadedAttachmentPreviews,
    getUploadedFileIds,
    uploadedAttachmentPreviews,
    uploadedFileIds,
    uploadAttachmentsAndGetUserFileIds,
  } = useNoraAttachmentUpload()

  const value = useMemo(
    () => ({
      attachments,
      canAttachMore,
      clearAttachments,
      isUploading,
      openAttachmentPicker,
      removeAttachment,
      getUploadedAttachmentPreviews,
      getUploadedFileIds,
      uploadedAttachmentPreviews,
      uploadedFileIds,
      uploadAttachmentsAndGetUserFileIds,
    }),
    [
      attachments,
      canAttachMore,
      clearAttachments,
      isUploading,
      openAttachmentPicker,
      removeAttachment,
      getUploadedAttachmentPreviews,
      getUploadedFileIds,
      uploadedAttachmentPreviews,
      uploadedFileIds,
      uploadAttachmentsAndGetUserFileIds,
    ],
  )

  return (
    <NoraFileAttachmentsContext.Provider value={value}>
      {children}
    </NoraFileAttachmentsContext.Provider>
  )
}

export const useNoraFileAttachments = () => useContext(NoraFileAttachmentsContext)
